/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-01-25 10:35:54
 */
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import type { RootState, AppDispatch } from './store'
import { useEffect, useMemo, useState } from 'react'
import CONFIG from '../constants/config'
import {
  fetchUdeskSign,
  setPaymentVisible,
  setAiChatDrawerVisible,
  setAgentCustomerDrawerVisible
} from '../actions/main'
import { sensorsRole } from '../utils/sensors'
import desktopLib from '../utils/desktopLib'
import { stripClientPrefix } from '../utils/clientAttribution'

// Use throughout your app instead of plain `useDispatch` and `useSelector`

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const useIsAdmin = () => {
  const myRole = useAppSelector((state) => state.group.myRole)
  const isAdmin = useMemo(() => {
    return (
      myRole === CONFIG.memberRoleTypes.ADMIN ||
      myRole === CONFIG.memberRoleTypes.MASTER_ADMIN
    )
  }, [myRole])

  return isAdmin
}

export const useVip = () => {
  const groupState = useAppSelector((state) => state.group)
  const isVip = groupState.isVip
  const isSVip =
    groupState.isVip && groupState.vipInfo?.vip_kind === CONFIG.VIP_KIND.SVIP
  const isVVip =
    groupState.isVip && groupState.vipInfo?.vip_kind === CONFIG.VIP_KIND.VVIP

  const benefits = groupState.vipInfo?.benefits || {}

  return { isVip, isSVip, isVVip, benefits }
}

export const useVipKind = (): number => {
  const groupState = useAppSelector((state) => state.group)
  const isVip = groupState.isVip
  const vipKind = groupState.vipInfo?.vip_kind
  return isVip ? (vipKind ?? 0) : 0
}

/**
 * 读后端下发的 AB 实验开关（state.main.abTestConfig.<key>）
 * 命中=true / 未命中=false/undefined。未取到时按未命中处理。
 */
export const useAbFlag = (key: string): boolean =>
  useAppSelector((state) => state.main.abTestConfig?.[key] === true)

/**
 * 存储/成员数据 · 从真实 statistics + group + groupLimit 拼装
 * 成员数取 statistics.memberCount（权威、与 NewStatistics 一致），兜底 group.groupSize；
 * 成员上限取 group.groupSizeLimit（全站「成员已达上限」判断来源）。
 * 不要用 limitInfo.peopleNum：部分团队该值为 0 会挡掉真实成员数。
 * limitInfo 仅用于 viewLimited（存储超限查看告警）。
 */
export const useEffectiveStorage = () => {
  const statistics = useAppSelector((state) => state.group.statistics)
  const groupSize = useAppSelector((state) => state.group.groupSize)
  const groupSizeLimit = useAppSelector((state) => state.group.groupSizeLimit)
  const limitInfo = useAppSelector((state) => state.groupLimit.limitInfo)

  const stat = (statistics ?? {}) as Record<string, number | undefined>
  return {
    totalNum: stat.totalNum ?? 0,
    limitPhotoNum: stat.limitPhotoNum ?? 0,
    memberCount: stat.memberCount ?? groupSize ?? 0,
    memberLimit: groupSizeLimit ?? 0,
    viewLimited: limitInfo?.photoViewLimit?.exist === true
  }
}

/**
 * 到期天数 · 透传调用方算好的真实值（调用方按 group.vipInfo.expire_time 计算）
 */
export const useEffectiveExpireDays = (realExpireDays: number): number =>
  realExpireDays

/**
 * 补差价升级资格 · 透传调用方 fetch 出来的真实值（fetchUpdateGoodsInfo）
 */
export const useEffectiveCanDiffUpgrade = (realValue: boolean): boolean =>
  realValue

export const useAiChatDrawer = () => {
  const dispatch = useAppDispatch()
  const close = () => dispatch(setAiChatDrawerVisible(false, null))
  const open = (info: any) => {
    dispatch(setAiChatDrawerVisible(true, info))
  }
  return { close, open }
}

export const useAgentCustomerDrawer = () => {
  const dispatch = useAppDispatch()
  const agentCustomerDrawerStatus = useAppSelector(
    (state) => state.main.agentCustomerDrawerStatus
  )
  const visible = agentCustomerDrawerStatus?.visible || false
  const info = agentCustomerDrawerStatus?.info || null

  const close = () => dispatch(setAgentCustomerDrawerVisible(false, null))

  const open = (drawerInfo: any) => {
    dispatch(setAgentCustomerDrawerVisible(true, drawerInfo))
  }

  const toggle = (drawerInfo) => {
    if (visible) {
      close()
    } else {
      open(drawerInfo)
    }
  }

  return {
    close,
    open,
    toggle,
    visible,
    info
  }
}

export const usePayment = () => {
  interface PaymentStatus {
    visible: boolean
    sourceDetail: string
    vipKind: number
    isFromToView: boolean
    onlySvip: boolean
    onlyVVip?: boolean
    /** 卡点来源驱动的动态文案插槽数据 · null/缺省 = 硬编码兜底 */
    customContent?: { variant: string; data: any } | null
  }

  const dispatch = useAppDispatch()
  const paymentStatus: PaymentStatus = useAppSelector(
    (state) => state.main.paymentStatus
  ) as PaymentStatus

  const close = () => dispatch(setPaymentVisible(false))

  const open = (sourceDetail = '') =>
    dispatch(setPaymentVisible(true, sourceDetail))

  const openSvip = (
    sourceDetail = '',
    isFromToView = false,
    onlySvip = true,
    onlyVVip = false,
    customContent: { variant: string; data: unknown } | null = null
  ) =>
    dispatch(
      setPaymentVisible(
        true,
        sourceDetail,
        onlyVVip ? CONFIG.VIP_KIND.VVIP : CONFIG.VIP_KIND.SVIP,
        isFromToView,
        onlySvip,
        onlyVVip,
        customContent
      )
    )

  const openVipByKind = (
    vipKind: number,
    sourceDetail = '',
    isFromToView = false,
    customContent: { variant: string; data: any } | null = null
  ) => {
    dispatch(
      setPaymentVisible(
        true,
        sourceDetail,
        vipKind,
        isFromToView,
        false,
        false,
        customContent as any
      )
    )
  }

  return { close, open, openSvip, openVipByKind, paymentStatus }
}

export const useSensorsRole = () => {
  const myRole = useAppSelector((state) => state.group.myRole)
  return sensorsRole(myRole)
}

export const useContactUrl = (fromPage) => {
  const url = 'https://huihaiyinhe.s4.udesk.cn/im_client/?web_plugin_id=30149'

  const user = useAppSelector((store) => store.main.user)
  const group = useAppSelector((store) => store.group)
  const dispatch = useAppDispatch()
  const [contactUrl, setContactUrl] = useState('')

  const vipInfo = group && group.vipInfo
  const isVip = group && group.isVip

  const getRole = (roleId) => {
    const item = CONFIG.memberRoles.find(({ id }) => id === roleId)

    return item?.role
  }

  useEffect(() => {
    dispatch(fetchUdeskSign()).then(
      ({ nonce, signature, timestamp, webToken: web_token }) => {
        const query = {
          c_cf_客户端: desktopLib.isDesktop,
          c_cf_来源页面: fromPage,
          c_cf_登陆手机号: user?.mobile,
          c_name: user?.nickname,
          c_cf_团队号: group && group.groupId,
          c_cf_角色: group && getRole(group.myRole),
          c_cf_团队会员类型:
            isVip && vipInfo && CONFIG.groupVipType[vipInfo.vip_type],
          nonce,
          timestamp,
          web_token,
          signature
        }

        const params = new URLSearchParams(
          Object.entries(query).reduce(
            (acc, [k, v]) => {
              if (v != null && v !== false) acc[k] = String(v)
              return acc
            },
            {} as Record<string, string>
          )
        )
        setContactUrl(`${url}?${params.toString()}`)
      }
    )
  }, [])

  return contactUrl
}

export const useUserId = () => {
  return useAppSelector((store) => store.main.user?.user_id)
}

export const useIsPaymentV2DownloadOnceRoute = () => {
  const paymentStatus = useAppSelector((state) => state.main.paymentStatus)
  const visible = paymentStatus?.visible
  const sourceDetail = paymentStatus?.sourceDetail
  const checkedPhotos = useAppSelector(
    (state) => (state.photo as any)?.checkedPhotos
  )
  const checkAll = useAppSelector((state) => (state.photo as any)?.checkAll)
  const contentCheckedPhotos = useAppSelector(
    (state) => state.photosByContent?.checkedPhotos
  )
  const contentUncheckedPhotos = useAppSelector(
    (state) => state.photosByContent?.unCheckedPhotos
  )
  const contentCheckAll = useAppSelector(
    (state) => state.photosByContent?.checkAll
  )
  const contentPhotosNum = useAppSelector(
    (state) => state.photosByContent?.photosNum || 0
  )
  const limitInfo = useAppSelector((state) => state.groupLimit.limitInfo)

  const contentCheckedNum = useMemo(() => {
    if (!contentCheckedPhotos && !contentCheckAll) return 0
    if (contentCheckAll) {
      const uncheckedSize =
        contentUncheckedPhotos instanceof Set
          ? contentUncheckedPhotos.size
          : Object.keys(contentUncheckedPhotos || {}).length
      return Math.max(0, contentPhotosNum - uncheckedSize)
    }
    return contentCheckedPhotos instanceof Set
      ? contentCheckedPhotos.size
      : Object.keys(contentCheckedPhotos || {}).length
  }, [
    contentCheckedPhotos,
    contentCheckAll,
    contentPhotosNum,
    contentUncheckedPhotos
  ])

  const legacyChecked =
    (checkedPhotos &&
      (checkedPhotos instanceof Set
        ? checkedPhotos.size > 0
        : Object.keys(checkedPhotos).length > 0)) ||
    checkAll

  // 'downloadOnce' 是功能路由开关（决定挂 PaymentV2 的按张付费页还是普通开通弹窗），
  // 不是纯来源标记。客户端里来源串带 todaywork_ 前缀，比较前必须剥掉，
  // 否则客户端里按张付费的支付页根本唤不起来
  return (
    !!visible &&
    stripClientPrefix(sourceDetail) === 'downloadOnce' &&
    (legacyChecked || contentCheckedNum > 0) &&
    !limitInfo?.photoViewLimit?.exist
  )
}

export const useGroup = () => {
  return useAppSelector((store) => store.group)
}

export const useGroupID = () => {
  return useAppSelector((state) => state.group.groupId)
}

export const usePathValue = () => {
  return useMemo(() => {
    const pathname = window.location.pathname

    if (
      pathname === '/photo/all' ||
      pathname === '/' ||
      pathname === '/photo'
    ) {
      return 'allPhoto'
    } else if (pathname === '/attendance/daily') {
      return 'attendanceDaily'
    } else if (pathname === '/attendance/period') {
      return 'attendancePeriod'
    } else if (pathname === '/watermark/data') {
      return 'watermarkData'
    } else if (pathname === '/watermark/list') {
      return 'watermarkList'
    } else if (pathname === '/watermark/editor') {
      return 'watermarkEditor'
    } else if (pathname === '/watermark/batchEditor') {
      return 'watermarkBatchEditor'
    } else if (pathname === '/photo/location') {
      return 'photoLocation'
    } else if (pathname === '/member/location') {
      return 'memberLocation'
    } else if (pathname === '/pathway') {
      return 'pathway'
    } else if (/\/photo\/folder\/add\/\d+/.test(pathname)) {
      return 'photoFolder'
    } else if (/^\/photo\/picker\??[^/]*$/.test(pathname)) {
      return 'photoPicker'
    } else {
      return pathname
    }
  }, [])
}
